/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/SearchCommandRow.tsx
 * FILE_ID: EMAIL-INBOX-SEARCH-COMMAND-ROW-001
 * PURPOSE: The inbox Search mail row, shared with the compose reference list.
 * WHY: A second search box would drift from operators, suggestions, and Refine.
 *      Scope tabs and the AI review strip stay on the inbox surface only.
 */
import { Search, X } from 'lucide-react'
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'

import EmailSearchCheatSheet from '../../components/EmailSearchCheatSheet'
import EmailSearchSuggestions from '../../components/EmailSearchSuggestions'
import { FilterSidebar } from '../../components/FilterSidebar'
import type { CategorySidebarConfig, SidebarFilters } from '../../components/filter-sidebar/types'
import { InboxRefineMenu } from '../../components/inbox-command/InboxRefineMenu'
import {
  countActiveRefinements,
  resolveInboxCommandDensity,
  type InboxCommandDensity,
} from '../../components/inbox-command/contracts'
import { SavedSearchesMenu } from '../../components/SavedSearchesMenu'
import type { FilterPill } from '../../lib/inboxFilterPills'
import { countNarrowingPills } from '../../lib/inboxFilterPills'
import {
  applySuggestion,
  type SearchSuggestion,
} from '../../lib/parseEmailSearchQuery'
import { DEFAULT_EMAIL_SORT, type EmailSortMode } from '../../lib/sortEmails'
import type { PersonalCategoryLike } from '../../lib/categoryConfig'
import { unmuteCategory } from '../../stores/mutedCategoriesStore'

export interface SearchCommandModel {
  searchQuery: string
  setSearchQuery: Dispatch<SetStateAction<string>>
  setSearchHighlight: Dispatch<SetStateAction<number>>
  searchHighlight: number
  searchFocused: boolean
  setSearchFocused: Dispatch<SetStateAction<boolean>>
  liveSearchSuggestions: SearchSuggestion[]
  debouncedSearchQuery: string
  kanbanSession?: { organisation?: { id?: number | null } | null } | null
  sortMode: EmailSortMode
  setSortMode: Dispatch<SetStateAction<EmailSortMode>>
  canGroupByMailbox: boolean
  groupByMailbox: boolean
  setGroupByMailbox: Dispatch<SetStateAction<boolean>>
  showSidebar: boolean
  setShowSidebar: Dispatch<SetStateAction<boolean>>
  sidebarCategories: CategorySidebarConfig[]
  sidebarFilters: SidebarFilters
  patchFilters: (patch: Partial<SidebarFilters>) => void
  clearAllFilters: () => void
  applyPreset: (filters: Record<string, unknown>) => void
  setShortcutsPanelOpen: Dispatch<SetStateAction<boolean>>
  smartSearchOn: boolean
  toggleSmartSearch: () => void
  stats?: { by_category?: Record<string, number> } | null
  selectedCategories: string[]
  setSelectedCategories: Dispatch<SetStateAction<string[]>>
  mutedCategories: string[]
  customCategories: PersonalCategoryLike[]
  toggleCategory: (key: string) => void
  applyImportantCategories: () => void
  allocationFilter: 'allocated' | 'unallocated' | undefined
  setAllocationFilter: Dispatch<SetStateAction<'allocated' | 'unallocated' | undefined>>
  hidePromotions: boolean
  setHidePromotions: Dispatch<SetStateAction<boolean>>
  hideCleared: boolean
  setHideCleared: Dispatch<SetStateAction<boolean>>
  classifyBusy: boolean
  handleClassifyUnprocessed: () => void | Promise<void>
  handleRecategorize: () => void | Promise<void>
  filterPills: FilterPill[]
}

function useCommandDensity() {
  const ref = useRef<HTMLDivElement>(null)
  const [density, setDensity] = useState<InboxCommandDensity>('compact')
  useEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      const width =
        Array.isArray(entry.borderBoxSize) && entry.borderBoxSize[0]
          ? entry.borderBoxSize[0].inlineSize
          : entry.contentRect.width
      setDensity(resolveInboxCommandDensity(width))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return { ref, density }
}

export function SearchCommandRow({
  vm,
  pinnedDensity,
}: {
  vm: SearchCommandModel
  /** Compose rail is a fixed narrow column; pin roomy so the same side buttons stay visible. */
  pinnedDensity?: InboxCommandDensity
}) {
  const measured = useCommandDensity()
  const density = pinnedDensity ?? measured.density
  const searchAnchorRef = useRef<HTMLDivElement>(null)
  const refineCount = countActiveRefinements({
    activeFilterPills: countNarrowingPills(vm.filterPills),
    hasNonDefaultOrder: vm.sortMode !== DEFAULT_EMAIL_SORT,
    groupByMailbox: vm.groupByMailbox,
    smartSearch: vm.smartSearchOn,
  })

  return (
    <div ref={measured.ref} className="flex min-w-0 items-center gap-1.5">
      <div ref={searchAnchorRef} className="relative min-w-[6rem] flex-1">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search mail"
          value={vm.searchQuery}
          onChange={(e) => {
            vm.setSearchQuery(e.target.value)
            vm.setSearchHighlight(0)
          }}
          onFocus={() => {
            vm.setSearchFocused(true)
            vm.setSearchHighlight(0)
          }}
          onBlur={() => {
            window.setTimeout(() => vm.setSearchFocused(false), 120)
          }}
          onKeyDown={(e) => {
            if (!vm.searchFocused || vm.liveSearchSuggestions.length === 0) return
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              vm.setSearchHighlight((index) =>
                Math.min(index + 1, vm.liveSearchSuggestions.length - 1),
              )
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              vm.setSearchHighlight((index) => Math.max(index - 1, 0))
            } else if (e.key === 'Enter' || e.key === 'Tab') {
              const suggestion = vm.liveSearchSuggestions[vm.searchHighlight]
              if (!suggestion) return
              e.preventDefault()
              vm.setSearchQuery(applySuggestion(vm.searchQuery, suggestion))
              vm.setSearchHighlight(0)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              vm.setSearchFocused(false)
            }
          }}
          className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-8 text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
          aria-label="Search emails"
          aria-haspopup="listbox"
          aria-expanded={vm.searchFocused}
        />
        {vm.searchQuery && vm.searchQuery !== vm.debouncedSearchQuery && (
          <span
            className="absolute right-7 top-1/2 h-3 w-3 -translate-y-1/2 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary"
            aria-label="Searching…"
          />
        )}
        {vm.searchQuery && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              vm.setSearchQuery('')
            }}
            className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {vm.searchFocused && (
          <EmailSearchSuggestions
            anchorRef={searchAnchorRef}
            suggestions={vm.liveSearchSuggestions}
            highlightedIndex={vm.searchHighlight}
            onHoverIndex={(index) => vm.setSearchHighlight(index)}
            onDismiss={() => vm.setSearchFocused(false)}
            onSelect={(sug: SearchSuggestion) => {
              vm.setSearchQuery(applySuggestion(vm.searchQuery, sug))
              vm.setSearchHighlight(0)
            }}
          />
        )}
      </div>

      {density !== 'compact' && <EmailSearchCheatSheet />}
      {density === 'roomy' && (
        <SavedSearchesMenu
          organisationId={vm.kanbanSession?.organisation?.id ?? null}
          query={vm.searchQuery}
          onApply={(query) => {
            vm.setSearchQuery(query)
            vm.setSearchHighlight(0)
          }}
        />
      )}
      <InboxRefineMenu
        activeCount={refineCount}
        sortMode={vm.sortMode}
        onSortMode={vm.setSortMode}
        canGroupByMailbox={vm.canGroupByMailbox}
        groupByMailbox={vm.groupByMailbox}
        onToggleGroupByMailbox={() =>
          vm.setGroupByMailbox((current) => !current)
        }
        showSidebar={vm.showSidebar}
        onToggleSidebar={() => vm.setShowSidebar(!vm.showSidebar)}
        advancedFilters={
          <FilterSidebar
            categories={vm.sidebarCategories}
            filters={vm.sidebarFilters}
            onPatch={vm.patchFilters}
            onClear={vm.clearAllFilters}
            onApplyPreset={vm.applyPreset}
          />
        }
        onOpenShortcuts={() => vm.setShortcutsPanelOpen(true)}
        smartSearch={vm.smartSearchOn}
        onToggleSmartSearch={vm.toggleSmartSearch}
        byCategory={vm.stats?.by_category}
        selectedCategories={vm.selectedCategories}
        mutedCategories={vm.mutedCategories}
        customCategories={vm.customCategories}
        onToggleCategory={vm.toggleCategory}
        onClearCategories={() => vm.setSelectedCategories([])}
        onApplyImportantPreset={vm.applyImportantCategories}
        onUnmuteCategory={unmuteCategory}
        allocation={vm.allocationFilter}
        onSelectAllocation={vm.setAllocationFilter}
        hidePromotions={vm.hidePromotions}
        onToggleHidePromotions={vm.setHidePromotions}
        hideCleared={vm.hideCleared}
        onToggleHideCleared={vm.setHideCleared}
        classifyBusy={vm.classifyBusy}
        onClassify={() => {
          void vm.handleClassifyUnprocessed()
        }}
        onRecategorize={() => {
          void vm.handleRecategorize()
        }}
      />
    </div>
  )
}
